import React, { useCallback, useMemo } from 'react';

import useTracking from 'components/Globals/Analytics';
import { COMPONENTS, SECTIONS } from 'components/Globals/Analytics/constants';

import {
  SUBSCRIPTION_GROUP_IDENTIFIERS,
  SUBSCRIPTION_PLANS,
  SUBSCRIPTION_PRODUCT_IDS,
  SUBSCRIPTIONS_STATUSES,
} from 'constants/subscriptions';

import useAppContext from 'utils/hooks/useAppContext';
import { useUserSubscriptions } from 'utils/hooks/useAuthenticatedUser';

import PlanCard from './PlanCard';
import classes from './PlanListing.module.scss';

const PlanListing = ({ groupIdentifier, onClick, subscription, isRedirecting, processingProductIds }) => {
  const { data: userSubscriptions } = useUserSubscriptions();
  const track = useTracking();
  const { paywall } = useAppContext();
  const plans = SUBSCRIPTION_PLANS[groupIdentifier];

  const currentPlan = useMemo(() => {
    if (!subscription && groupIdentifier !== SUBSCRIPTION_GROUP_IDENTIFIERS.ARCHIVE_ACCESS) {
      return null;
    }

    return plans.find(
      plan =>
        plan.productId === subscription?.product?.id ||
        (plan.productId === SUBSCRIPTION_PRODUCT_IDS.ARCHIVE_ACCESS &&
          userSubscriptions.some(
            sub =>
              sub.subscription?.product?.id === SUBSCRIPTION_PRODUCT_IDS.ARCHIVE_ACCESS &&
              sub.status === SUBSCRIPTIONS_STATUSES.active,
          )),
    );
  }, [subscription, groupIdentifier, plans, userSubscriptions]);

  const hasIndividualCastingPlan = useMemo(() => {
    if (groupIdentifier !== SUBSCRIPTION_GROUP_IDENTIFIERS.CASTING_TOOL) {
      return false;
    }

    return userSubscriptions.some(
      sub =>
        sub.subscription?.product?.id === SUBSCRIPTION_PRODUCT_IDS.CASTING_TOOL &&
        sub.status === SUBSCRIPTIONS_STATUSES.active,
    );
  }, [userSubscriptions, groupIdentifier]);

  const hasCurrentPlan = !!currentPlan;
  const hasTieredPlans = !!currentPlan?.tier;

  const onClickHandler = useCallback(
    plan => {
      onClick(plan);

      track.click({
        section: SECTIONS.PAYWALL,
        component: COMPONENTS.PAYWALL_PLAN_CARD,
        meta: {
          id: plan.productId,
          actionType: paywall?.paywallType,
        },
      });
    },
    [onClick, track, paywall?.paywallType],
  );

  return (
    <div className={classes.root}>
      <div className={classes.planListing}>
        {plans?.map((plan, index) => {
          const isCurrentPlan = hasCurrentPlan && currentPlan?.productId === plan?.productId;
          const canPurchase = !hasCurrentPlan;
          const canUpgrade = hasCurrentPlan && hasTieredPlans && !isCurrentPlan && currentPlan?.tier > plan?.tier;
          const canDowngrade = hasCurrentPlan && hasTieredPlans && !isCurrentPlan && currentPlan?.tier < plan?.tier;
          const isCastingPlan = plan.productId === SUBSCRIPTION_PRODUCT_IDS.CASTING_TOOL;

          return (
            <PlanCard
              key={index}
              plan={plan}
              onClick={onClickHandler}
              canPurchase={canPurchase}
              canUpgrade={canUpgrade}
              canDowngrade={canDowngrade}
              isCurrentPlan={isCurrentPlan || (isCastingPlan && hasIndividualCastingPlan)}
              isRedirecting={isRedirecting}
              isProcessing={processingProductIds?.includes(plan.productId)}
            />
          );
        })}
      </div>
    </div>
  );
};

export default PlanListing;
